import type {
  CreateInvitationRequest,
  CreateInvitationResponse,
  LoginRequest,
  LogoutRequest,
  OrgMemberListResponse,
  TokenPairResponse,
  TotpEnrollRequest,
  TotpEnrollmentResponse,
  TotpRequiredResponse,
} from "@bitacorapro/api-types";
import { request } from "../client";

// /auth/login returns a discriminated union (ADR-0024). Non-admin users
// always get a TokenPairResponse (no `kind` field). Admin users with
// totp_enabled=false get TotpEnrollmentResponse on first call; with
// totp_enabled=true they get TotpRequiredResponse if the request body
// omitted the 6-digit code, or a TokenPairResponse if it carried a
// valid one.
export type LoginResponse = TokenPairResponse | TotpRequiredResponse | TotpEnrollmentResponse;

export const authApi = {
  login: (body: LoginRequest): Promise<LoginResponse> =>
    request<LoginResponse>("POST", "auth", "/auth/login", {
      body,
      skipAuth: true,
    }),
  logout: (body: LogoutRequest): Promise<null> =>
    request<null>("POST", "auth", "/auth/logout", { body }),
  // `opts.view: "roster"` is the opt-in filtered view (recipe-declared
  // roles only, plus a server-computed `isOwner` field per row) that
  // Equipo.tsx's roster render uses. Default (no opts) stays the
  // unfiltered response — DetalleObservacion.tsx's actor-name
  // resolution (via resolveUserDisplayName/describeTimelineActor in
  // lib/util.ts) depends on seeing every member, not just recipe-
  // declared roles, so `useMemberships()`'s default call must never
  // pick up the filter.
  listMemberships: (opts?: { view?: "roster" }): Promise<OrgMemberListResponse> => {
    const qs = opts?.view ? `?view=${encodeURIComponent(opts.view)}` : "";
    return request<OrgMemberListResponse>("GET", "auth", `/auth/memberships${qs}`);
  },
  // POST /auth/totp/enroll — second step of first-login TOTP for
  // platform admins. Re-authenticates with email + password and
  // verifies the 6-digit code; on success the server flips
  // totp_enabled=true and returns the JWT pair.
  enrollTotp: (body: TotpEnrollRequest): Promise<TokenPairResponse> =>
    request<TokenPairResponse>("POST", "auth", "/auth/totp/enroll", {
      body,
      skipAuth: true,
    }),
  // POST /auth/invitations — works in both modes:
  //   - org-admin: body.organizationId is ignored, JWT's org wins.
  //   - platform-admin: body.organizationId targets any org (the only
  //     way to seed the first admin of a freshly-provisioned org from
  //     the internal panel).
  // Sends the invitation email via Resend; response includes the raw
  // acceptUrl so the operator can also copy/paste it if needed.
  createInvitation: (body: CreateInvitationRequest): Promise<CreateInvitationResponse> =>
    request<CreateInvitationResponse>("POST", "auth", "/auth/invitations", { body }),
};
